import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Globe,
  Plus,
  Settings,
  Terminal,
  X,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LogoDark from '../assets/logo-dark.svg';
import Logo from '../assets/logo.svg';
import { usePlausible } from '../hooks/use-plausible';
import { useCurrentPlan } from '../hooks/useCurrentPlan';
import { useUserStore } from '../store/useUserStore';
import { formatDistanceToNow } from 'date-fns';
import { CompactUsageIndicator } from './compact-usage-indicator';
import { NavUser } from './nav-user';
import { OrganizationSelector } from './organization-selector';
import { useTheme } from './theme-provider';
import { Button } from './ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from './ui/sidebar';
import { useWebSocket } from '@/contexts/WebSocketContext';
import type { Channel } from 'phoenix';
import { createDebugEndpoint, fetchDebugEndpoints } from '../lib/api';
import { DebugEndpoint } from '../types/debug-endpoints';
import {
  uniqueNamesGenerator,
  Config,
  adjectives,
  colors,
  animals
} from 'unique-names-generator';
import { toast } from 'sonner';

interface NavItem {
  title: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  url: string;
}

interface SidebarRequestActivityPayload {
  timestamp?: string;
}

// Core navigation - focused on webhook inspection
const navItems: NavItem[] = [
  {
    title: 'Endpoints',
    icon: Globe,
    url: '/',
  },
  {
    title: 'Device Codes',
    icon: Terminal,
    url: '/device-codes',
  },
];

export function AppSidebar() {
  const { user } = useUserStore();
  const { trackPlausibleEvent } = usePlausible();
  const { data: currentPlan } = useCurrentPlan();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { socket, isConnected } = useWebSocket();
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [lastActivity, setLastActivity] = useState<{
    timestamp: string;
    endpointName?: string;
  } | null>(null);
  const endpointChannelsRef = useRef<Record<string, Channel>>({});

  // Fetch recent endpoints for the activity feed
  const { data: endpoints } = useQuery<DebugEndpoint[]>({
    queryKey: ['debug-endpoints'],
    queryFn: fetchDebugEndpoints,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 30, // Auto-refresh every 30 seconds
  });

  // Quick endpoint creation
  const createQuickEndpoint = async (): Promise<DebugEndpoint> => {
    const config: Config = {
      dictionaries: [adjectives, colors, animals],
      separator: '-',
      length: 3,
    };
    const generatedName = uniqueNamesGenerator(config);

    return await createDebugEndpoint({
      debug_endpoint: {
        name: generatedName,
        status: 'active',
      },
    });
  };

  const quickEndpointMutation = useMutation<DebugEndpoint, Error, void>({
    mutationFn: createQuickEndpoint,
    onSuccess: (createdEndpoint) => {
      queryClient.invalidateQueries({ queryKey: ['debug-endpoints'] });
      toast.success('Endpoint created', {
        description: `${createdEndpoint.name} is ready to receive webhooks`,
      });
      navigate({ to: `/endpoints/${createdEndpoint.id}` });
    },
    onError: (error) => {
      if (error instanceof Error && error.message.includes('403')) {
        toast.error('Endpoint limit reached', {
          description: 'You have reached the maximum number of endpoints allowed for your plan.',
        });
      } else {
        toast.error('Failed to create endpoint', {
          description: 'Please try again or contact support if the problem persists.',
        });
      }
    },
  });

  const handleQuickCreate = () => {
    trackPlausibleEvent('quick_endpoint_create');
    quickEndpointMutation.mutate();
  };

  useEffect(() => {
    if (location.pathname.startsWith('/endpoints')) {
      setPendingRequestCount(0);
      setLastActivity(null);
    }
  }, [location.pathname]);

  const showActivityIndicator =
    Boolean(user) && isConnected && pendingRequestCount > 0;

  const lastActivityDescription = lastActivity
    ? `${lastActivity.endpointName ?? 'Webhook endpoint'} ${formatDistanceToNow(
        new Date(lastActivity.timestamp),
        { addSuffix: true }
      )}`
    : 'Incoming webhook ready to inspect';

  const handleAcknowledgeActivity = () => {
    setPendingRequestCount(0);
    setLastActivity(null);
  };

  const handleViewWebhookActivity = () => {
    handleAcknowledgeActivity();
    navigate({ to: '/' });
  };

  // Get recent endpoints (sorted by created_at, limit to 5)
  const recentEndpoints = useMemo(() => {
    if (!endpoints) {
      return [];
    }
    return [...endpoints]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      .slice(0, 5);
  }, [endpoints]);
  const recentEndpointMetaKey = recentEndpoints
    .map((endpoint) => `${endpoint.id}:${endpoint.name ?? ''}`)
    .sort()
    .join(',');

  const sidebarLinkClass =
    'flex items-center gap-2 w-full rounded-md transition-colors border border-transparent py-0.5';
  const sidebarLinkHoverClass = 'hover:bg-white/5';
  const sidebarActiveLinkClass =
    'flex items-center gap-2 w-full rounded-md transition-colors border bg-linear-to-r from-purple-500/10 to-blue-500/10 text-primary border-white/10 shadow-xs py-0.5';

  // Determine resolved theme
  const resolvedTheme =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;

  // Use logo.svg for dark theme, logo-dark.svg for light theme
  const logoSrc = resolvedTheme === 'dark' ? Logo : LogoDark;

  // Determine button text based on plan status
  const getPlanButtonText = () => {
    if (!currentPlan || currentPlan.name?.includes('free')) {
      return 'Upgrade Plan';
    }
    if (currentPlan.is_canceling) {
      return 'Resume Plan';
    }
    return 'Manage Plan';
  };

  const planButtonText = getPlanButtonText();

  useEffect(() => {
    if (!socket || !isConnected || !user) {
      Object.values(endpointChannelsRef.current).forEach((channel) => channel.leave());
      endpointChannelsRef.current = {};
      return;
    }

    if (recentEndpoints.length === 0) {
      return;
    }

    const endpointMap = new Map(
      recentEndpoints.map((endpoint) => [endpoint.id, endpoint.name ?? endpoint.id])
    );

    Object.keys(endpointChannelsRef.current).forEach((endpointId) => {
      if (!endpointMap.has(endpointId)) {
        endpointChannelsRef.current[endpointId].leave();
        delete endpointChannelsRef.current[endpointId];
      }
    });

    endpointMap.forEach((endpointName, endpointId) => {
      if (endpointChannelsRef.current[endpointId]) {
        return;
      }

      const channel = socket.channel(`debug:endpoint:${endpointId}`, {});
      channel
        .join()
        .receive('ok', () => {
          console.log(`Sidebar subscribed to endpoint ${endpointId}`);
        })
        .receive('error', (resp) => {
          console.error(`Sidebar failed to join endpoint ${endpointId}`, resp);
        })
        .receive('timeout', () => {
          console.error(`Sidebar timeout joining endpoint ${endpointId}`);
        });

      channel.on('request_captured', (payload: SidebarRequestActivityPayload) => {
        setPendingRequestCount((prev) => prev + 1);
        setLastActivity({
          timestamp: payload.timestamp ?? new Date().toISOString(),
          endpointName,
        });
      });

      endpointChannelsRef.current[endpointId] = channel;
    });
  }, [
    socket,
    isConnected,
    user,
    recentEndpointMetaKey,
    recentEndpoints,
  ]);

  useEffect(() => {
    return () => {
      Object.values(endpointChannelsRef.current).forEach((channel) => channel.leave());
      endpointChannelsRef.current = {};
    };
  }, []);

  // Format relative time
  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Sidebar className="border-r border-white/10 backdrop-blur-xs bg-background/50">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-0 top-0 h-32 w-32 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute right-0 bottom-0 h-32 w-32 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <SidebarHeader className="px-4 py-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="group">
              <Link
                to="/"
                className="flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors hover:bg-white/5 cursor-pointer"
              >
                <div className="flex aspect-square size-10 items-center justify-center rounded-lg bg-linear-to-br from-purple-500/20 to-blue-500/20 ring-1 ring-white/10 shadow-lg group-hover:shadow-purple-500/10 transition-shadow">
                  <img src={logoSrc} alt="Hooklistener" className="w-6 h-6" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold tracking-tight">
                    Hooklistener
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Organization Selector */}
      {user && (
        <>
          <div className="px-4 py-2">
            <OrganizationSelector />
          </div>
          <div className="mx-4 border-t border-white/10" />
        </>
      )}

      <SidebarContent className="px-4 space-y-2 pt-2">
        {showActivityIndicator && (
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs shadow-inner shadow-black/20">
            <div className="relative">
              <span className="absolute inset-0 animate-ping rounded-full bg-purple-500/30" />
              <Activity className="relative h-4 w-4 text-purple-400" />
            </div>
            <div className="flex-1 leading-tight">
              <p className="font-medium text-foreground">{pendingRequestCount} new webhook{pendingRequestCount > 1 ? 's' : ''}</p>
              <p className="text-[11px] text-muted-foreground">{lastActivityDescription}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-purple-300 hover:text-white"
              onClick={handleViewWebhookActivity}
            >
              View
            </Button>
            <button
              type="button"
              onClick={handleAcknowledgeActivity}
              className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
              aria-label="Dismiss activity indicator"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <Link
                    to={item.url}
                    className={`${sidebarLinkClass} ${sidebarLinkHoverClass}`}
                    activeProps={{
                      className: sidebarActiveLinkClass,
                    }}
                  >
                    <SidebarMenuButton className="py-2">
                      <item.icon className="w-4 h-4" />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Quick Actions */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium text-muted-foreground px-2 py-1">
            Quick Actions
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <Button
              onClick={handleQuickCreate}
              disabled={quickEndpointMutation.isPending}
              className="w-full bg-linear-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white border-0 shadow-lg hover:shadow-purple-500/20 transition-all"
            >
              <Plus className="w-4 h-4 mr-2" />
              {quickEndpointMutation.isPending ? 'Creating...' : 'New Endpoint'}
            </Button>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Recent Endpoints */}
        {recentEndpoints.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground px-2 py-1">
              Recent Endpoints
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {recentEndpoints.map((endpoint) => (
                  <SidebarMenuItem key={endpoint.id}>
                    <Link
                      to="/endpoints/$id"
                      params={{ id: endpoint.id }}
                      className="flex items-center justify-between gap-2 w-full px-2 py-1.5 rounded-md hover:bg-white/5 transition-colors text-sm cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="truncate text-foreground/80">
                          {endpoint.name}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {getRelativeTime(endpoint.created_at)}
                      </span>
                    </Link>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
              {endpoints && endpoints.length > 5 && (
                <Link
                  to="/"
                  className="block px-2 py-1 text-xs text-purple-500 hover:text-purple-400 transition-colors cursor-pointer"
                >
                  View all →
                </Link>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <div className="mt-auto">
        {user && (
          <div className="px-4 mb-2">
            <CompactUsageIndicator debugEndpointsCount={endpoints?.length} />
          </div>
        )}
        <SidebarGroup className="mb-2 px-4">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Link to="/organization/settings/billing" className="w-full">
                  <Button
                    className="group w-full bg-purple-500 text-white hover:bg-purple-600 border-0 relative cursor-pointer"
                    onClick={() => trackPlausibleEvent('upgrade_plan_click')}
                  >
                    <span>{planButtonText}</span>
                  </Button>
                </Link>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarFooter className="px-2 py-0 border-t border-white/10">
          {user ? (
            <NavUser user={user} className="p-2" />
          ) : (
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-purple-500 text-white font-medium hover:bg-purple-600 transition-colors cursor-pointer"
            >
              <Settings className="w-4 h-4" />
              Sign in
            </Link>
          )}
        </SidebarFooter>
      </div>
    </Sidebar>
  );
}
